 
import React, { FC, useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { replace } from '@aarhus-university/au-lib-react-components/src/lib/helpers';
import { useLangContext } from '@aarhus-university/au-lib-react-components/src/lib/context';
import { getContextBasedHeadline, getCountryOptions, validateSpecialCharacters } from '../../lib/helpers';
import { emailRegex } from '../../lib/common';
import ErrorComponent from '../common/ErrorComponent';
import DateOfBirthSelectComponent from '../common/DateOfBirthSelectComponent';
import ReceiptComponent from './ReceiptComponent';
import { useTranslationsContext } from '../../lib/translation';
import { useRegistrationContext } from '../../lib/context';
import { useUserSignupByFormMutation } from '../../services/api';

const CreateFormComponent: FC = () => {
  const {
    register, handleSubmit, formState: { errors }, setError, control,
  } = useForm<IUserSignupByFormUser>();
  const [selectedMonth, selectedYear] = useWatch({ control, name: ['dateOfBirth_month', 'dateOfBirth_year'] });

  const lang = useLangContext();
  const translations = useTranslationsContext();
  const context = useRegistrationContext();
  const [userSignupByForm, response] = useUserSignupByFormMutation();
  const { data: userSignupByFormResult, error, isLoading: uiBusy } = response;

  useEffect(() => {
    if (userSignupByFormResult && userSignupByFormResult.result === 'UnderAge' && !errors.dateOfBirth_day) {
      setError('dateOfBirth_day', {
        type: 'manual',
        message: translations.userUnderAge,
      });
    }
  });

  const onSubmit = (data: IUserSignupByFormUser) => {
    const date = new Date(
      data.dateOfBirth_year,
      (+data.dateOfBirth_month - 1),
      data.dateOfBirth_day,
    );

    /* eslint eqeqeq: 0 */
    const isValidDate = (Boolean(+date) && date.getDate() == data.dateOfBirth_day);
    if (!isValidDate) {
      setError('dateOfBirth_day', {
        type: 'manual',
        message: translations.invalidDate,
      });
    } else {
      userSignupByForm({
        user: {
          ...data,
          dateOfBirth: new Date(Date.UTC(
            data.dateOfBirth_year,
            data.dateOfBirth_month - 1,
            data.dateOfBirth_day,
          )),
          preferredLanguage: 'en',
        },
        context: context?.key as string,
        culture: lang,
      });
    }
  };

  if (!context) {
    return null;
  }

  if (userSignupByFormResult && (userSignupByFormResult.result === 'AccessGrantedToNewUser'
    || userSignupByFormResult.result === 'MatchingExistingUserFound'
    || userSignupByFormResult.result === 'EmailVerificationRequired'
    || userSignupByFormResult.result === 'FuzzyMatchIdentityVerificationRequired')) {
    return (
      <ReceiptComponent
        confirmDetails={userSignupByFormResult}
      />
    );
  }

  let disclaimer = translations.disclaimerDefaultText;
  const { disclaimerDa, disclaimerEn } = context as ISignupContext;
  if (lang === 'da' && disclaimerDa !== null) {
    disclaimer = disclaimerDa;
  } else if (lang === 'en' && disclaimerEn !== null) {
    disclaimer = disclaimerEn;
  }

  let buttonClass = 'button';
  if (uiBusy) {
    buttonClass = `${buttonClass} button--processing`;
  }

  if (error != null) {
    buttonClass = `${buttonClass} visually-disabled`;
  }

  return (
    <>
      <div className="page__content__block">
        <div className="text--stamp">{getContextBasedHeadline(lang, context as ISignupContext)}</div>
        <h1>{translations.loginUsingForm}</h1>
        <form className="form" noValidate onSubmit={handleSubmit(onSubmit)}>
          <div className={errors.firstName ? 'form__field form-info--error' : 'form__field'}>
            <label htmlFor="firstName">
              {translations.firstName}
            </label>
            {errors.firstName
              && (
                <span className="form-info__message">
                  {errors.firstName.message}
                </span>
              )}
            <input
              type="text"
              id="firstName"
              disabled={uiBusy}
              {...register('firstName', {
                required: {
                  value: true,
                  message: translations.firstNameIsRequired,
                },
                maxLength: {
                  value: 32,
                  message: translations.firstNameTooLong,
                },
                validate: (value: string) => validateSpecialCharacters(value) || translations.nameMustNotContainSpecialCharacters,
              })}
            />
          </div>
          <div className={errors.lastName ? 'form__field form-info--error' : 'form__field'}>
            <label htmlFor="lastName">
              {translations.lastName}
            </label>
            {errors.lastName
              && (
                <span className="form-info__message">
                  {errors.lastName.message}
                </span>
              )}
            <input
              type="text"
              id="lastName"
              disabled={uiBusy}
              {...register('lastName', {
                required: {
                  value: true,
                  message: translations.lastNameIsRequired,
                },
                maxLength: {
                  value: 32,
                  message: translations.lastNameTooLong,
                },
                validate: (value: string) => validateSpecialCharacters(value) || translations.nameMustNotContainSpecialCharacters,
              })}
            />
          </div>
          <div className={errors.privateEmail ? 'form__field form-info--error' : 'form__field'}>
            <label htmlFor="email">
              {translations.privateEmail}
            </label>
            {errors.privateEmail
              && (
                <span className="form-info__message">
                  {errors.privateEmail.message}
                </span>
              )}
            <input
              type="text"
              id="privateEmail"
              disabled={uiBusy}
              {...register('privateEmail', {
                required: {
                  value: true,
                  message: translations.privateEmailIsRequired,
                },
                pattern: {
                  value: emailRegex,
                  message: translations.invalidEmail,
                },
              })}
            />
          </div>
          <div className={errors.gender ? 'form__field form-info--error' : 'form__field'}>
            <label htmlFor="gender">{translations.gender}</label>
            {errors.gender
              && (
                <span className="form-info__message">
                  {errors.gender.message}
                </span>
              )}
            <select
              id="gender"
              disabled={uiBusy}
              defaultValue=""
              {...register('gender', {
                required: {
                  value: true,
                  message: translations.genderIsRequired,
                },
              })}
            >
              <option value="">{translations.selectPlaceholder}</option>
              <option value="unknown">{translations.genderUnknown}</option>
              <option value="female">{translations.genderFemale}</option>
              <option value="male">{translations.genderMale}</option>
            </select>
          </div>
          <div className={errors.nationality ? 'form__field form-info--error' : 'form__field'}>
            <label htmlFor="nationality">{translations.nationality}</label>
            {errors.nationality
              && (
                <span className="form-info__message">
                  {errors.nationality.message}
                </span>
              )}
            <select
              id="nationality"
              disabled={uiBusy}
              {...register('nationality', {
                required: {
                  value: true,
                  message: translations.nationalityIsRequired,
                },
              })}
            >
              <option value="">{translations.selectPlaceholder}</option>
              {getCountryOptions(lang, translations.stateless).map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
          </div>
          <DateOfBirthSelectComponent
            dayRegistration={register('dateOfBirth_day', {
              required: { value: true, message: translations.dayIsRequired },
            })}
            monthRegistration={register('dateOfBirth_month', {
              required: { value: true, message: translations.monthIsRequired },
            })}
            yearRegistration={register('dateOfBirth_year', {
              required: { value: true, message: translations.yearIsRequired },
            })}
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            errorMessages={[
              errors.dateOfBirth_day?.message,
              errors.dateOfBirth_month?.message,
              errors.dateOfBirth_year?.message,
            ]}
            disabled={uiBusy}
          />
          <div className={errors.disclaimerCheck ? 'form__field form__field--horizontal form-info--error' : 'form__field form__field--horizontal'}>
            <label htmlFor="disclaimerCheck">
              {disclaimer}
              {errors.disclaimerCheck
                && (
                  <span className="form-info__message">
                    {errors.disclaimerCheck.message}
                  </span>
                )}
            </label>
            <input
              id="disclaimerCheck"
              type="checkbox"
              {...register('disclaimerCheck', {
                validate: (x) => {
                  if (x === false) {
                    return translations.mustAcceptDisclaimer;
                  }
                  return true;
                },
              })}
              disabled={uiBusy}
            />
          </div>

          <div className="submit button-container">
            <button type="submit" className={buttonClass} disabled={uiBusy || error != null}>{translations.next}</button>
          </div>
        </form>
      </div>
      {
        error && (
          <div className="page__content__block">
            <ErrorComponent
              header={translations.errorHeader}
              text={replace(translations.errorBody, ['context'], [context.key])}
            />
          </div>
        )
      }
    </>
  );
};

CreateFormComponent.displayName = 'CreateFormComponent';
export default CreateFormComponent;
